<template>
    <slot />
</template>

<script setup>
    import { useEventBus } from '@vueuse/core';
    import { FrontSide, LinearFilter, LinearMipmapLinearFilter, MeshBasicMaterial, MeshPhysicalMaterial } from 'three';
    import { computed, inject, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';

    import { lerp } from '@resn/gozer-math';
    import { requestIdleCallback } from '@resn/gozer-misc';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { usePane, useRafBool, useRenderer } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import { shaderChunks as glslShine } from '@/components/gl/shaders/ShaderChunks/transitions/shine';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { createLogger } from '~/utils/logger';
    import { applyCardTextureRotation } from '~/utils/card-texture-utils';

    const logger = createLogger('gl.card-inner');

    // Define props with default values
    const props = defineProps({
        active: { default: false },
        object: { default: null },
        imgUrl: { default: null },
        side: { default: FrontSide },
        flip: { default: false },
        layer: { default: 0 },
        name: { default: 'Card Inner' },
        grader: { defaut: 'psa' },
        direction: { default: undefined }, // specifies the orientation of the card texture
    });

    // Reactive references for material properties
    const { renderer } = useRenderer();
    const { depthOffset, revealPr, inProgress } = inject('card');
    const visible = computed(() => revealPr.value > 0.0);

    const metalness = ref(0.0);
    const roughness = ref(1);
    const iridescence = ref(0.3);
    const iridescenceIOR = ref(1.3);
    const color = ref('white');
    const envMapIntensity = ref(3);
    const alphaTest = ref(0.5);

    const bus = useEventBus('card-inner');

    const material = new MeshPhysicalMaterial({
        name: 'Card Inner',
        transparent: true,
        defines: {
            USE_UV: true,
            // USE_DEBUG: true,
            DISTANCE: true,
            USE_TRANSITION_SHINE: false,
        },
    });
    // const debugMaterial = new MeshBasicMaterial();

    // Configure the object
    let objectMain;

    // Set up global assets (environment map and alpha map)
    const loader = useGlobalAssets(({ data }) => {
        const cardId = `card-mask-${props.grader}`;

        material.envMap = data.envMapPMREM || data.envMap;
        material.alphaMap = data[cardId];
    });

    const setVisible = () => {
        objectMain.visible = visible.value;
    };

    const setObject = () => {
        if (objectMain) objectMain.parent.remove(objectMain);

        objectMain = props.object;
        objectMain.rotation.y = props.flip ? Math.PI : 0;
        objectMain.material = material;
        // objectMain.material = debugMaterial;

        if (loader.assets) {
            const data = loader.assets;
            const cardId = `card-mask-${props.grader}`;
            material.alphaMap = data[cardId];
        }

        setVisible();

        return objectMain;
    };

    objectMain = setObject();

    // Custom shader modification
    const uniforms = {
        progress: { value: 0 },
        shinePosition: { value: 0 },
        alpha: { value: 1 },
    };

    const animateShine = (value = 1, duration = 0.75, ease = 'power2.inOut', delay = 0) => {
        // Reset progress before animation
        uniforms.shinePosition.value = 0;
        gsap.killTweensOf(uniforms.shinePosition);
        // Return the tween for external control
        return gsap.to(uniforms.shinePosition, { value, duration, ease, delay });
    };

    material.onBeforeCompile = (shader) => {
        shader.uniforms.progress = uniforms.progress;
        shader.uniforms.shinePosition = uniforms.shinePosition;
        shader.uniforms.alpha = uniforms.alpha;
        // Add varying to vertex shader
        shader.vertexShader = shader.vertexShader.replace(
            `#include <common>`,
            `
            #include <common>
            varying vec3 vWorldPosition;
            varying vec3 vPosition;
            `
        );

        // Set the varying in vertex shader
        shader.vertexShader = shader.vertexShader.replace(
            `#include <fog_vertex>`,
            `
            #include <fog_vertex>	
            vWorldPosition = worldPosition.xyz;   
            vPosition = position.xyz;    
            `
        );

        // Add progress uniform and varying to fragment shader
        shader.fragmentShader = shader.fragmentShader.replace(
            `#include <common>`,
            `
            #include <common>            

            ${glslShine.uniforms}
            ${glslShine.funcs}
            uniform float progress; 
            uniform float shinePosition;
            uniform float alpha;
            varying vec3 vWorldPosition;
            varying vec3 vPosition;
            `
        );

        // Modify fragment color based on progress
        shader.fragmentShader = shader.fragmentShader.replace(
            `#include <dithering_fragment>`,
            `
            if (gl_FrontFacing == false) discard;
                
            gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(0.0), progress) ;
            #ifdef USE_TRANSITION_SHINE
                gl_FragColor.rgb = transition_shine(gl_FragColor.xyz, vWorldPosition, 10.0, 0.5, 0.5);
                gl_FragColor.rgb = transition_shine(gl_FragColor.xyz, vWorldPosition, 10.0, 0.5, -0.25);
            #endif

            gl_FragColor.a *= alpha;
            
            #ifdef USE_DEBUG
                // gl_FragColor = vec4(envMapColor.rgb, 1.0);
                gl_FragColor = vec4(sampledDiffuseColor.rgb, 1.0);
            #endif

            `
        );
    };

    // Function to set texture
    const setTexture = (url) => {
        if (!url) {
            logger.warnOnce('Card texture URL missing', {
                event: 'asset.texture_missing',
                onceKey: `asset.texture_missing:CardInner:${props.name}:surface`,
                textureKind: 'surface',
                component: 'CardInner',
                side: props.name,
            });
            return;
        }

        loadBitmapTexture(url, {
            flipY: true,
            minFilter: LinearMipmapLinearFilter,
        }).then((texture) => {
            material.map?.dispose();
            const scale = props.grader === 'sgc' ? 1.03 : 1;

            applyCardTextureRotation(props.name, props.direction, texture, scale);
            material.map = texture;
            material.roughnessMap = texture;
            material.needsUpdate = true;
            // debugMaterial.map = texture;
            // debugMaterial.needsUpdate = true;

            bus.emit('texture-loaded');

            requestIdleCallback(() => {
                renderer.initTexture(texture);
            });
        });
    };

    // Options for debug UI
    const options = { min: 0, max: 1, step: 0.01 };

    // Update material properties
    watchEffect(() => {
        material.metalness = metalness.value;
        material.roughness = roughness.value;
        material.iridescence = iridescence.value;
        material.iridescenceIOR = iridescenceIOR.value;
        material.envMapIntensity = envMapIntensity.value;
        material.alphaTest = alphaTest.value;

        uniforms.progress.value = lerp(inProgress.value, 1, depthOffset.value);
    });

    // Watch for changes in visible and imgUrl
    watch(visible, setVisible);
    watch(() => props.imgUrl, setTexture);
    watch(() => props.object, setObject);

    // Handle object position
    const pos = ref({ x: 0, y: 0, z: 0 });
    watchEffect(() => {
        objectMain.position.set(pos.value.x, pos.value.y, pos.value.z);
    });

    // Lifecycle hooks
    onMounted(() => {
        if (props.imgUrl) setTexture(props.imgUrl);
    });

    useRafBool(visible, ({ timestamp }) => {
        // objectMain.visible = vis;
    });

    onBeforeUnmount(() => {
        disposeObject(objectMain);
    });

    // Debugging UI setup
    usePane(
        [
            { name: 'visible', value: visible },
            { name: 'pos', value: pos },
            { name: 'metalness', value: metalness, options },
            { name: 'roughness', value: roughness, options },
            { name: 'iridescence', value: iridescence, options },
            {
                name: 'iridescenceIOR',
                value: iridescenceIOR,
                options: { min: 1, max: 2, step: 0.01 },
            },
            { name: 'color', value: color },
            { name: 'alphaTest', value: alphaTest, options },
            {
                name: 'envMapIntensity',
                value: envMapIntensity,
                options: { min: 0, max: 10, step: 0.1 },
            },
        ],
        { title: props.name, expanded: false }
    );

    defineExpose({ animateShine });
</script>

<style lang="scss" scoped></style>
